import { motion, type Variants } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Download,
  Shield,
} from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';

interface BackupCodesProps {
  backupCodes: string[];
  onComplete: () => void;
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
};

export function BackupCodes({ backupCodes, onComplete }: BackupCodesProps) {
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleCopy = async () => {
    try {
      const codesText = backupCodes.join('\n');
      await navigator.clipboard.writeText(codesText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy backup codes:', error);
    }
  };

  const handleDownload = () => {
    const codesText = `Dashboard Planalto Capital - Códigos de Backup 2FA\n\nGuarde estes códigos em um local seguro. Cada código só pode ser usado uma vez.\n\n${backupCodes.join('\n')}\n\nData de geração: ${new Date().toLocaleString('pt-BR')}`;

    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backup-codes-2fa-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setDownloaded(true);
  };

  const handleComplete = () => {
    if (!confirmed) {
      setConfirmed(true);
      return;
    }
    onComplete();
  };

  return (
    <motion.div
      className="w-full max-w-2xl bg-white rounded-2xl shadow-xl p-8 md:p-10"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(142,76%,36%)] to-[hsl(142,76%,30%)] flex items-center justify-center mx-auto mb-4 shadow-lg">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-[hsl(210,50%,10%)] mb-2">
          Códigos de Backup
        </h1>
        <p className="text-[hsl(215,16%,47%)] text-sm">
          Guarde estes códigos em um local seguro
        </p>
      </motion.div>

      <motion.div variants={itemVariants}>
        <Alert className="mb-6 border-[hsl(43,56%,61%)] bg-[hsl(43,56%,90%)]">
          <AlertTriangle className="h-4 w-4 text-[hsl(43,56%,40%)]" />
          <AlertDescription className="text-[hsl(43,56%,30%)] text-sm">
            <strong>Importante:</strong> Estes códigos são exibidos apenas uma
            vez. Cada código só pode ser usado uma única vez.
          </AlertDescription>
        </Alert>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-[hsl(210,40%,98%)] p-4 rounded-xl mb-6"
      >
        <div className="grid grid-cols-2 gap-2 font-mono text-sm">
          {backupCodes.map((code, index) => (
            <div
              key={index}
              className="bg-white p-3 rounded-lg border border-[hsl(214,32%,91%)] text-center font-semibold text-[hsl(210,50%,10%)]"
            >
              {code}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 mb-6"
      >
        <Button
          variant="outline"
          onClick={handleCopy}
          className="flex-1 h-11 border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,98%)] gap-2"
          disabled={copied}
        >
          {copied ? (
            <>
              <CheckCircle className="w-4 h-4 text-[hsl(142,76%,36%)]" />
              Copiado!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copiar Códigos
            </>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={handleDownload}
          className="flex-1 h-11 border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,98%)] gap-2"
        >
          <Download className="w-4 h-4" />
          {downloaded ? 'Baixado!' : 'Baixar como .txt'}
        </Button>
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="pt-6 border-t border-[hsl(214,32%,91%)] mb-6"
      >
        <h3 className="font-semibold text-sm text-[hsl(210,50%,10%)] mb-3">
          Dicas de Segurança:
        </h3>
        <ul className="space-y-1 text-sm text-[hsl(215,16%,47%)]">
          <li>• Guarde os códigos em um gerenciador de senhas</li>
          <li>• Armazene uma cópia física em local seguro</li>
          <li>• Não compartilhe estes códigos com ninguém</li>
          <li>• Cada código só pode ser usado uma vez</li>
        </ul>
      </motion.div>

      <motion.div variants={itemVariants}>
        {!confirmed ? (
          <div className="space-y-4">
            <Alert className="border-[hsl(43,56%,61%)] bg-[hsl(43,56%,90%)]">
              <AlertTriangle className="h-4 w-4 text-[hsl(43,56%,40%)]" />
              <AlertDescription className="text-[hsl(43,56%,30%)] text-sm">
                Certifique-se de que você salvou os códigos antes de continuar.
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleComplete}
              variant="outline"
              className="w-full h-11 border-[hsl(214,32%,91%)] text-[hsl(210,50%,10%)] hover:bg-[hsl(210,40%,98%)]"
            >
              Eu salvei os códigos de backup
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert className="border-[hsl(142,76%,36%)] bg-[hsl(142,76%,95%)]">
              <CheckCircle className="h-4 w-4 text-[hsl(142,76%,36%)]" />
              <AlertDescription className="text-[hsl(142,76%,25%)] text-sm">
                <strong>2FA configurado com sucesso!</strong> Você agora pode
                acessar o dashboard com segurança.
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleComplete}
              className="w-full h-11 bg-[hsl(210,35%,32%)] hover:bg-[hsl(210,35%,25%)] text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Acessar Dashboard
            </Button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
